import {
  rightsCategories,
  rightsList,
  roleDefinitions,
  vendorRightsCategories,
  vendorRightsList,
  vendorRoleDefinitions,
} from "./constants";
import { getUserSubRoleNames } from "./overviewContactsShared";

export const DEFAULT_NEW_SPONSOR_SUBROLES = ["Sponsor Admin"];
export const DEFAULT_NEW_VENDOR_SUBROLES = ["Vendor Admin"];
export const DEFAULT_VENDOR_REQUEST_CONTACT_DESIGNATION = "request";

const VENDOR_ADMIN_EXCLUDED_RIGHTS = new Set([
  "Data Rooms",
  "Trust Pages",
  "All Questionnaires",
]);

const isOptionalSponsorGeneralRight = (right, canGrantChatDiligentAccess) =>
  right === "Trial Experience Dashboard" ||
  (!canGrantChatDiligentAccess && right === "Access to Chat Diligent");

/** Rights that should be enabled for the given sponsor sub-roles (create/edit forms). */
export function getSponsorRightsEnabledForRoles(
  selectedRoles,
  { canGrantChatDiligentAccess = false } = {},
) {
  const enabled = new Set();
  if (!selectedRoles?.length) return enabled;

  rightsCategories["General Permissions"].rights
    .filter((right) => !isOptionalSponsorGeneralRight(right, canGrantChatDiligentAccess))
    .forEach((right) => enabled.add(right));

  selectedRoles.forEach((role) => {
    if (role === "Sponsor Admin") {
      (roleDefinitions["Sponsor Admin"]?.defaultRights ?? []).forEach((right) => {
        enabled.add(right);
      });
    } else if (role === "RFI Manager") {
      rightsCategories["RFI Permissions"].rights.forEach((right) => enabled.add(right));
    } else if (role === "VQA Manager") {
      rightsCategories["Assessment Permissions"].rights.forEach((right) =>
        enabled.add(right),
      );
    }
  });

  if (
    selectedRoles.includes("RFI Manager") ||
    selectedRoles.includes("Sponsor Admin")
  ) {
    enabled.add("My Vendors");
  }

  enabled.delete("Trial Experience Dashboard");
  if (!canGrantChatDiligentAccess) {
    enabled.delete("Access to Chat Diligent");
  }

  return enabled;
}

/** Rights that should be enabled for the given vendor sub-roles (create/edit forms). */
export function getVendorRightsEnabledForRoles(selectedRoles) {
  const enabled = new Set();
  if (!selectedRoles?.length) return enabled;

  vendorRightsCategories["General Permissions"].rights.forEach((right) =>
    enabled.add(right),
  );

  selectedRoles.forEach((role) => {
    if (role === "Vendor Admin") {
      (vendorRoleDefinitions["Vendor Admin"]?.defaultRights ?? []).forEach((right) => {
        enabled.add(right);
      });
    } else if (role === "RFI Manager - Approver" || role === "RFI Manager - User") {
      vendorRightsCategories["RFI Permissions"].rights.forEach((right) => {
        if (!VENDOR_ADMIN_EXCLUDED_RIGHTS.has(right)) enabled.add(right);
      });
    } else if (role === "VQA Manager") {
      vendorRightsCategories["Assessment Permissions"].rights.forEach((right) =>
        enabled.add(right),
      );
    }
  });

  return enabled;
}

export function applySponsorPermissionsForRoles(
  setValue,
  selectedRoles,
  options = {},
) {
  const {
    canGrantChatDiligentAccess = false,
    preserveTrialDashboard = false,
    preserveChatDiligent = false,
    getValues,
    setValueOptions = { shouldDirty: true },
  } = options;

  rightsList.forEach((right) => {
    setValue(right, false, setValueOptions);
  });

  const enabled = getSponsorRightsEnabledForRoles(selectedRoles, {
    canGrantChatDiligentAccess,
  });

  if (preserveTrialDashboard && getValues?.("Trial Experience Dashboard") === true) {
    enabled.add("Trial Experience Dashboard");
  }
  if (preserveChatDiligent && getValues?.("Access to Chat Diligent") === true) {
    enabled.add("Access to Chat Diligent");
  }

  rightsList.forEach((right) => {
    setValue(right, enabled.has(right), setValueOptions);
  });

  if (!enabled.has("Trial Experience Dashboard")) {
    setValue("Trial Experience Dashboard", false, setValueOptions);
  }
}

export function applyVendorPermissionsForRoles(setValue, selectedRoles, setValueOptions = {}) {
  vendorRightsList.forEach((right) => {
    setValue(right, false, setValueOptions);
  });

  const enabled = getVendorRightsEnabledForRoles(selectedRoles);
  vendorRightsList.forEach((right) => {
    setValue(right, enabled.has(right), setValueOptions);
  });
}

export function isUserRoleSelectionSaveDisabled(isLoading, selectedRoles) {
  return Boolean(isLoading) || !selectedRoles?.length;
}

/** react-hook-form `role` must stay a string[]; hidden inputs and API payloads can break that. */
export function normalizeFormRoleSelection(raw) {
  if (raw == null || raw === "") return [];
  const hasObjectEntries =
    Array.isArray(raw) && raw.some((entry) => entry && typeof entry === "object");
  return getUserSubRoleNames({
    sub_roles: hasObjectEntries ? undefined : raw,
    vendor_subrole_dto: hasObjectEntries ? raw : undefined,
    provider_user_subrole: hasObjectEntries ? raw : undefined,
  });
}
